/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import {
    Star, MapPin, Building2, BadgeCheck, ArrowRight, ShieldCheck,
    MessageSquare, Calendar,
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

/**
 * ConvReseauProfile — Iter164
 * Re-positionné côté DIRIGEANT (le client économique principal).
 * On retire toute mention de portabilité / « profil qui vous appartient »
 * du premier plan. Ces arguments sont conservés pour /pour-les-agents
 * et la FAQ.
 */
export const ConvReseauProfile = () => {
    return (
        <section className="relative py-20 md:py-32 border-t border-white/5" data-testid="reseau-profile-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        Réputation collective
                    </span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.6 }}
                        className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
                        style={DISPLAY}
                    >
                        Des profils professionnels{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            qui renforcent toute votre agence.
                        </span>
                    </motion.h2>
                    <p className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed">
                        Chaque accompagnement vendeur valorise le travail du professionnel
                        et enrichit la réputation collective de l'agence. Les agents
                        développent une identité professionnelle vérifiée, tandis que
                        l'agence conserve ses données, ses avis et l'historique de son
                        activité.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    {/* Carte profil mockup */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.55 }}
                        className="relative"
                        data-testid="agent-mockup-card"
                    >
                        <div className="absolute -inset-6 bg-gradient-to-br from-[#681793]/30 to-[#FF914D]/15 blur-3xl rounded-3xl pointer-events-none" />
                        <div className="relative rounded-3xl bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-white/10 p-7 md:p-8 backdrop-blur-sm">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#A855F7] to-[#FF914D] flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                                    LF
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-lg font-semibold text-white" style={DISPLAY}>
                                            Laure Fontanieu
                                        </h3>
                                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#A855F7]/15 text-[#C7A1F0] border border-[#A855F7]/20"
                                              style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                            <BadgeCheck className="w-3 h-3" />
                                            Profil vérifié
                                        </span>
                                    </div>
                                    <p className="text-sm text-[#A19DAB] mt-1 flex items-center gap-1.5">
                                        <Building2 className="w-3.5 h-3.5" />
                                        Immo Excellence Lyon
                                    </p>
                                    <p className="text-sm text-[#A19DAB] mt-0.5 flex items-center gap-1.5">
                                        <MapPin className="w-3.5 h-3.5" />
                                        Lyon 6e · Brotteaux · Tête d'Or
                                    </p>
                                </div>
                            </div>

                            <div className="mt-5 flex items-center gap-3 flex-wrap">
                                <div className="flex items-center gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 text-[#FF914D] fill-[#FF914D]" />
                                    ))}
                                </div>
                                <span className="text-sm text-white/85 font-medium">4,9</span>
                                <span className="text-xs text-[#6E6A7A]">· 28 avis vendeurs vérifiés</span>
                            </div>

                            {/* Bandeau agence : note collective */}
                            <div className="mt-4 rounded-xl border border-[#A855F7]/15 bg-[#A855F7]/[0.06] p-3 flex items-center gap-3">
                                <Building2 className="w-4 h-4 text-[#C7A1F0] flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] uppercase tracking-wider text-[#C7A1F0] font-medium"
                                       style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                        Immo Excellence Lyon
                                    </p>
                                    <p className="text-xs text-white/75">
                                        L'agence est aussi évaluée à la fin du parcours vendeur.
                                    </p>
                                </div>
                                <span className="text-sm text-white/85 font-medium">4,8/5</span>
                            </div>

                            <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-3 gap-3 text-center">
                                <div>
                                    <p className="text-xs text-[#6E6A7A]">Suivi</p>
                                    <p className="text-base font-semibold text-white mt-0.5">96 %</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#6E6A7A]">Accompagnements</p>
                                    <p className="text-base font-semibold text-white mt-0.5">142</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#6E6A7A]">Expérience</p>
                                    <p className="text-base font-semibold text-white mt-0.5">15 ans</p>
                                </div>
                            </div>

                            <div className="mt-6 flex gap-2">
                                <button className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white text-[#1A1224] font-medium text-xs sm:text-sm py-2.5 rounded-full whitespace-nowrap"
                                        type="button" disabled>
                                    <MessageSquare className="w-3.5 h-3.5" />
                                    Contacter
                                </button>
                                <button className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-medium text-xs sm:text-sm py-2.5 rounded-full whitespace-nowrap"
                                        type="button" disabled>
                                    <Calendar className="w-3.5 h-3.5" />
                                    Être estimé
                                </button>
                            </div>
                            <p className="text-[10px] text-[#6E6A7A] text-center mt-3 italic">
                                Aperçu illustratif — données de démonstration.
                            </p>
                        </div>
                    </motion.div>

                    {/* Bénéfices côté agence */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.55, delay: 0.08 }}
                        className="space-y-5"
                    >
                        {[
                            {
                                icon: BadgeCheck,
                                title: 'Valorisez les compétences de chaque professionnel',
                                desc: "Chaque agent dispose d'une identité professionnelle vérifiée qui rend visible son savoir-faire.",
                            },
                            {
                                icon: ShieldCheck,
                                title: 'Renforcez la confiance accordée à votre agence',
                                desc: "L'affiliation est confirmée, l'agence est aussi évaluée à la fin du parcours vendeur, l'historique reste dans le cockpit.",
                            },
                            {
                                icon: Star,
                                title: 'Transformez les accompagnements en preuves de qualité',
                                desc: "Chaque avis est rattaché à un dossier vendeur réel suivi dans la plateforme — pas un commentaire anonyme.",
                            },
                            {
                                icon: Building2,
                                title: 'Attirez vendeurs et nouveaux talents',
                                desc: "Une agence où les agents sont reconnus publiquement attire plus de mandats vendeurs et de candidats qualifiés.",
                            },
                        ].map(({ icon: Icon, title, desc }) => (
                            <div key={title}
                                 className="flex gap-4 p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                                <div className="w-10 h-10 rounded-xl bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                                    <Icon className="w-5 h-5 text-[#A855F7]" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-white mb-1" style={DISPLAY}>
                                        {title}
                                    </h4>
                                    <p className="text-sm text-[#A19DAB] leading-relaxed">{desc}</p>
                                </div>
                            </div>
                        ))}

                        <a href="/pour-les-agences"
                           className="group inline-flex items-center gap-2 mt-2 px-5 py-3 rounded-full bg-white/[0.04] border border-white/15 hover:border-[#FF914D]/40 hover:bg-[#FF914D]/10 text-sm font-semibold text-white transition-all"
                           data-testid="link-pour-agences">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF914D] animate-pulse" />
                            Voir le pitch dédié aux dirigeants d'agence
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
